import React, { useEffect, useRef, useState } from 'react'
import { usePromoCodesQuery } from '@graphql/queries/__generated__/promo-codes.generated'
import { PromoCodeFragment } from '@graphql/fragments/__generated__/PromoCode.generated'
import { Dialog, DialogContent, SwipeableDrawer, Tooltip, useMediaQuery, useTheme } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import ArrowForwardIosIcon from '@icons/ArrowForwardIosIcon'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { useQueryParams, StringParam } from 'use-query-params'
import { useTranslation } from 'react-i18next'
import FormattedDate from '@components/FormattedDate'
import { decodeHtml } from '../../../utils/misc'

declare global {
  interface Window {
    // Set by templates/storefront/index.html: whether the promoCodes query will return anything.
    HAS_PROMO_CODES?: boolean
  }
}

interface Props {
  hide?: boolean
}

export const PROMO_CODES_DIALOG_PARAMS = {
  promoCodesDialog: StringParam,
}

const ACCENT = '#005058'
const BAR_BG = '#dcece8'
const ROTATE_DELAY_MS = 10000
const COPIED_TOOLTIP_MS = 2000
// Horizontal finger travel that switches to the neighbouring promo code on touch screens.
const SWIPE_MIN_PX = 40
// The product card's image arrows (ReflectionCard.tsx), at the bar's size.
const arrowSx = {
  color: 'secondary.main',
  '&:hover': { bgcolor: 'transparent', color: '#69c0b2' },
  '& svg': { fontSize: 18 },
} as const

// `copied` is the key of the copy button that was clicked, so only that button shows the "Copied" tooltip (the bar
// and the dialog both have one for the same code).
const useCopyToClipboard = (): [string | null, (key: string, text: string) => void] => {
  const [copied, setCopied] = useState<string | null>(null)
  useEffect(() => {
    if (!copied) return undefined
    const timer = window.setTimeout(() => setCopied(null), COPIED_TOOLTIP_MS)
    return () => window.clearTimeout(timer)
  }, [copied])
  const copy = (key: string, text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => setCopied(key))
      .catch(err => console.error('Failed to copy:', err))
  }
  return [copied, copy]
}

const PromoCodes: React.FC<Props> = ({ hide }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [params, setParams] = useQueryParams(PROMO_CODES_DIALOG_PARAMS)
  const [index, setIndex] = useState(0)
  // Which side the next code slides in from: 1 - from the right (next), -1 - from the left (previous), 0 - the first
  // code shown on page load, which fades in instead (the bar's place is already reserved, see showBar).
  const [direction, setDirection] = useState(0)
  // How far the finger has dragged the current code, while swiping.
  const [dragX, setDragX] = useState(0)
  const [paused, setPaused] = useState(false)
  const [copied, copy] = useCopyToClipboard()
  const { data, loading } = usePromoCodesQuery({
    variables: {
      first: 10,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })

  const promoCodes: PromoCodeFragment[] = data?.promoCodes.edges.map(edge => edge.node) || []
  const count = promoCodes.length
  const current = count ? promoCodes[index % count] : null
  const dialogPromoCode = promoCodes.find(node => node.id === params.promoCodesDialog)
  // Keeps the content while the dialog fades out after the query param is cleared.
  const lastDialogPromoCode = useRef<PromoCodeFragment | null>(null)
  if (dialogPromoCode) lastDialogPromoCode.current = dialogPromoCode
  const shownPromoCode = dialogPromoCode || lastDialogPromoCode.current

  useEffect(() => {
    if (count < 2 || paused || dialogPromoCode) return undefined
    const timer = window.setInterval(() => {
      setDirection(1)
      setIndex(i => (i + 1) % count)
    }, ROTATE_DELAY_MS)
    return () => window.clearInterval(timer)
  }, [count, paused, !!dialogPromoCode])

  const onOpen = (id: string) => () => {
    setParams({ promoCodesDialog: id }, 'replaceIn')
  }
  const onClose = () => {
    setParams({ promoCodesDialog: undefined }, 'replaceIn')
  }
  const onCopy = (key: string, token: string) => (e: React.MouseEvent) => {
    e.stopPropagation()
    copy(key, token)
  }
  const onStep = (step: number) => () => {
    setDirection(step)
    setDragX(0)
    setIndex(i => (i + step + count) % count)
  }

  // The code follows the finger; released past SWIPE_MIN_PX it is replaced by the neighbouring one, which slides in
  // from the opposite side, otherwise it springs back.
  const touchStart = useRef<{ x: number; y: number; horizontal: boolean | null } | null>(null)
  const onTouchStart = (e: React.TouchEvent) => {
    if (count < 2) return
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, horizontal: null }
  }
  const onTouchMove = (e: React.TouchEvent) => {
    const start = touchStart.current
    if (!start) return
    const dx = e.touches[0].clientX - start.x
    const dy = e.touches[0].clientY - start.y
    // Decided once per gesture, so a mostly vertical swipe scrolls the page without moving the code.
    if (start.horizontal === null && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
      start.horizontal = Math.abs(dx) > Math.abs(dy)
    }
    if (start.horizontal) setDragX(dx)
  }
  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touchStart.current
    touchStart.current = null
    if (!start?.horizontal) return
    const dx = e.changedTouches[0].clientX - start.x
    if (Math.abs(dx) < SWIPE_MIN_PX) {
      setDragX(0)
      return
    }
    // Swiping left brings the next code, like a carousel.
    onStep(dx < 0 ? 1 : -1)()
  }

  // The server tells whether there are promo codes, so the bar takes its height before the query returns instead of
  // pushing the page down when it does. It only collapses if the query unexpectedly comes back empty.
  const showBar = !hide && (count > 0 || (loading && !data && !!window.HAS_PROMO_CODES))

  // "<name> - Code <TOKEN>", with the token highlighted. Names often already end with "Use code <TOKEN>": then the
  // token is highlighted in place instead of being repeated.
  const renderBarText = (node: PromoCodeFragment) => {
    if (!node.token) return node.name
    const token = <span className='font-semibold text-secondary'>{node.token}</span>
    const at = node.name.toLowerCase().indexOf(node.token.toLowerCase())
    if (at >= 0) {
      return (
        <>
          {node.name.slice(0, at)}
          {token}
          {node.name.slice(at + node.token.length)}
        </>
      )
    }
    return (
      <>
        {node.name} - {t('promoCodePanel.code', 'Code')} {token}
      </>
    )
  }

  const copyButton = (key: string, token: string, className = '') => (
    <Tooltip
      title={t('message.copiedToClipboard', 'Copied to clipboard')}
      open={copied === key}
      placement='bottom'
      disableHoverListener
      disableFocusListener
      disableTouchListener
    >
      <IconButton
        size='small'
        onClick={onCopy(key, token)}
        aria-label={t('button.copyCode', 'Copy code')}
        sx={{ color: ACCENT }}
        className={className}
      >
        <ContentCopyIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Tooltip>
  )

  const dialogContent = shownPromoCode && (
    <>
      <span className='absolute right-10 top-10'>
        <IconButton onClick={onClose} title={t('close', 'Close')} size='small'>
          <CloseIcon />
        </IconButton>
      </span>
      <div className='text-11 font-semibold uppercase tracking-wider mb-8 text-secondary'>
        {t('promoCodePanel.title', 'Promo code')}
      </div>
      <div className='text-xl font-semibold leading-snug pr-30 mb-15'>{shownPromoCode.name}</div>
      {!!shownPromoCode.token && (
        <div
          className='flex items-center justify-between rounded-full pl-20 pr-5 py-4 mb-15'
          style={{ backgroundColor: BAR_BG, border: `1px dashed ${ACCENT}` }}
        >
          <span className='font-semibold tracking-wider text-secondary'>{shownPromoCode.token}</span>
          {copyButton('dialog', shownPromoCode.token)}
        </div>
      )}
      {!!shownPromoCode.description && (
        <div className='text-base text-gray whitespace-pre-line' style={{ lineHeight: 1.6 }}>
          {decodeHtml(shownPromoCode.description)}
        </div>
      )}
      {!!shownPromoCode.endDate && (
        <div className='text-13 text-gray mt-10'>
          {t('promoCodePanel.validUntil', 'Valid until')} <FormattedDate value={shownPromoCode.endDate} />
        </div>
      )}
    </>
  )

  return (
    <>
      {showBar && (
        // The extra 20px at the bottom sits under the header's rounded top corners (_header.scss).
        <div
          data-promo-bar
          className='relative -mb-20 pb-20'
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={() => {
            touchStart.current = null
            setDragX(0)
          }}
          // Vertical swipes still scroll the page; horizontal ones are left to onTouchEnd. The z-index keeps the bar
          // above the mobile menu's backdrop (MobileMenu.tsx, z-index 20), the drawer itself starts below the bar; the
          // header (z-index 50) still covers the bar's bottom 20px.
          style={{ backgroundColor: BAR_BG, touchAction: 'pan-y', zIndex: 30 }}
        >
          <div className='flex items-center justify-center h-[45px] px-5 sm:px-10'>
            {count > 1 && (
              <IconButton
                size='small'
                onClick={onStep(-1)}
                aria-label={t('button.previous', 'Previous')}
                sx={arrowSx}
                className='promo-bar__appear'
              >
                <ArrowForwardIosIcon viewBox='0 -960 960 960' sx={{ transform: 'rotate(180deg)' }} />
              </IconButton>
            )}
            {/* A fixed width, so the arrows stay in place when the codes rotate. Longer names are cut with an ellipsis. */}
            <div className='flex items-center justify-center w-[400px] min-w-0 max-w-full mx-5 sm:mx-10 overflow-hidden'>
              {current && (
                <div
                  key={current.id}
                  className={`promo-bar__item ${
                    direction ? `promo-bar__item--from-${direction > 0 ? 'right' : 'left'}` : 'promo-bar__appear'
                  } flex items-center min-w-0`}
                  style={{
                    transform: dragX ? `translateX(${dragX}px)` : undefined,
                    opacity: dragX ? Math.max(1 - Math.abs(dragX) / 200, 0.3) : undefined,
                    transition: touchStart.current ? 'none' : 'transform 0.2s ease-out, opacity 0.2s ease-out',
                  }}
                >
                  <button
                    type='button'
                    data-promo-open
                    className='min-w-0 truncate bg-transparent border-0 p-0 cursor-pointer text-13 sm:text-base text-black normal-case tracking-normal font-normal'
                    onClick={onOpen(current.id)}
                    title={t('button.details', 'Details')}
                  >
                    {renderBarText(current)}
                  </button>
                  {!!current.token && copyButton('bar', current.token, 'flex-shrink-0 !ml-5')}
                </div>
              )}
            </div>
            {count > 1 && (
              <IconButton
                size='small'
                onClick={onStep(1)}
                aria-label={t('button.next', 'Next')}
                sx={arrowSx}
                className='promo-bar__appear'
              >
                <ArrowForwardIosIcon viewBox='0 -960 960 960' />
              </IconButton>
            )}
          </div>
        </div>
      )}
      {isMobile ? (
        // A bottom sheet on phones: swiping it down closes it.
        <SwipeableDrawer
          anchor='bottom'
          open={!!dialogPromoCode}
          onClose={onClose}
          onOpen={() => undefined}
          disableSwipeToOpen
          swipeAreaWidth={0}
          PaperProps={{ sx: { borderTopLeftRadius: '20px', borderTopRightRadius: '20px', maxHeight: '85vh' } }}
        >
          <div className='flex justify-center pt-10 pb-2'>
            <span className='block w-40 h-4 rounded-full bg-gray-400' />
          </div>
          <div className='relative overflow-y-auto px-20 pt-10 pb-30'>{dialogContent}</div>
        </SwipeableDrawer>
      ) : (
        <Dialog
          open={!!dialogPromoCode}
          onClose={onClose}
          maxWidth='xs'
          fullWidth
          PaperProps={{ sx: { borderRadius: '20px' } }}
        >
          <DialogContent sx={{ p: '28px 28px 24px' }}>{dialogContent}</DialogContent>
        </Dialog>
      )}
    </>
  )
}

export default React.memo(PromoCodes)
